import {
  afterNextRender,
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import { outputFromObservable, takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { nameofFactory, nil } from "@ankaadia/ankaadia-shared";
import { translateSafely } from "@ankaadia/ankaadia-shared-frontend";
import {
  GetAggregatedMilestoneStatesInput,
  InstanceStateEnum,
  GetAggregatedMilestoneStatesQuery,
  GetMilestoneCandidateStatesInput,
  GlobalMilestoneWithMeta,
  MilestoneCandidateStateFragment,
  MilestoneScheduleBreakdownFragment,
  MilestoneScheduleSegmentFragment,
  ProcessMilestoneListForDropdownFragment,
  StaticDataType,
} from "@ankaadia/graphql";
import { isEmpty, isNil, uniqBy } from "lodash";
import { TranslocoService } from "@jsverse/transloco";
import { MenuItem, PrimeIcons } from "primeng/api";
import { Breadcrumb } from "primeng/breadcrumb";
import { Message } from "primeng/message";
import { ProgressSpinner } from "primeng/progressspinner";
import { Tooltip } from "primeng/tooltip";
import { filter, finalize, forkJoin, map, of, startWith, switchMap } from "rxjs";
import { AppDatePipe } from "../../../shared/pipes/date.pipe";
import { StaticDataPipe } from "../../../shared/static-data/static-data.pipe";
import { TranslateDirective } from "../../../shared/transloco/translate.directive";
import { NewUiTableComponent } from "../../../shared/table/new-ui-table.component";
import {
  PipeDescription,
  TableColumn,
  TableOperation,
  TableOperationMode,
  TableTag,
} from "../../../shared/table/table.model";
import { FilterMetadataMap } from "../../collections/collection-edit-assigned-candidates/custom-filter.model";
import { FlatTask } from "../../process/all-tasks/flat-task.model";
import { NewUiFlatTaskTableComponent } from "../../process/all-tasks/new-ui-flat-task-table.component";
import {
  NewUiProcessTaskActionDrawerComponent,
  ProcessTaskAction,
} from "../../process/process-task-action-drawer/new-ui-process-task-action-drawer.component";
import {
  ProcessTaskPermissionContext,
  ProcessTaskPermissionService,
} from "../../process/process-task-permission.service";
import { ProcessHelper } from "../../process/process.helper";
import {
  MilestoneAggregateSummary,
  MilestoneScheduleBreakdown,
  MilestoneScheduleDistribution,
  MilestoneScheduleSegment,
  MilestoneTimelineRendererService,
  TimelineTooltip,
} from "../milestone-timeline-renderer.service";
import { MilestoneViewService } from "../milestone-view.service";

type ScheduledMilestone = GlobalMilestoneWithMeta & MilestoneScheduleDistribution;

type AggregatedMilestoneStates = GetAggregatedMilestoneStatesQuery["getAggregatedMilestoneStates"];

type MilestoneListRequest = GetAggregatedMilestoneStatesInput;

type MilestoneCandidatesRequest = GetMilestoneCandidateStatesInput;

interface MilestoneCandidateRow {
  id: string;
  name: string;
  displayId: string;
  country: string;
  process: string;
  // Only the first one is linked; a candidate is usually at one milestone in just one process
  processLink: unknown[] | null;
  openTaskCount: number;
  plannedEndDate: MilestoneCandidateStateFragment["endDate"];
  delayInDays: MilestoneCandidateStateFragment["delayInDays"];
  activeTaskIds: MilestoneCandidateStateFragment["activeTaskIds"];
}

// Where the hovered tooltip target sits, relative to this component's host
interface TooltipPlacement {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface OpenedTaskAction {
  item: FlatTask;
  action: ProcessTaskAction;
}

const nameOf = nameofFactory<MilestoneCandidateRow>();

// Sorting the inbox by priority or sorting criteria means nothing in a single candidate's task list
const HIDDEN_TASK_COLUMNS = ["task.priority", "sortingCriteria"];

// Until the table has rendered a row to measure, and never fewer rows than this per page
const CANDIDATE_ROW_HEIGHT_FALLBACK = 41;
const PAGINATOR_HEIGHT_FALLBACK = 56;
const MIN_CANDIDATE_ROWS_PER_PAGE = 5;
// A milestone with a single candidate further behind plan than this gets the bolt, however well the rest run
const CRITICAL_DELAY_IN_DAYS = 10;
// Room left over below the last row, so borders and rounding don't tip the table into scrolling
const CANDIDATE_TABLE_SCROLLBAR_OFFSET = 10;

@Component({
  selector: "new-ui-milestone-aggregated-planning-view",
  templateUrl: "./new-ui-milestone-aggregated-planning-view.component.html",
  imports: [
    NewUiFlatTaskTableComponent,
    NewUiTableComponent,
    NewUiProcessTaskActionDrawerComponent,
    Breadcrumb,
    Message,
    ProgressSpinner,
    Tooltip,
    TranslateDirective,
  ],
  // Positions the bolt tooltip's anchor - relative rather than fixed, since the fullscreen dialog's
  // transform would otherwise become the fixed anchor's containing block
  host: { class: "relative" },
})
export class NewUiMilestoneAggregatedPlanningViewComponent implements AfterViewInit {
  private readonly milestoneViewService = inject(MilestoneViewService);
  private readonly timelineRendererService = inject(MilestoneTimelineRendererService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly taskPermissions = inject(ProcessTaskPermissionService);
  private readonly processHelper = inject(ProcessHelper);
  private readonly transloco = inject(TranslocoService);

  protected readonly processLanguage = this.transloco.getActiveLang();
  protected readonly hiddenTaskColumns = HIDDEN_TASK_COLUMNS;

  protected readonly timelineContainer = viewChild<ElementRef>("timelineContainer");

  // The timeline is drawn by d3 and its elements cannot carry a pTooltip themselves, so an invisible anchor
  // is laid over the hovered one and its tooltip opened by hand - styled like every other tooltip in the app
  private readonly timelineTooltip = viewChild.required("timelineTooltip", { read: Tooltip });
  protected readonly tooltipPlacement = signal<TooltipPlacement | null>(null);
  protected readonly tooltipText = signal("");
  private readonly criticalTooltipText = computed(() => {
    const reason = translateSafely("milestones.timeline.schedule.criticalTooltip", { days: CRITICAL_DELAY_IN_DAYS });
    if (!this.fullscreen()) {
      return reason;
    }

    return `${reason}. ${translateSafely("milestones.timeline.schedule.criticalTooltipClick")}`;
  });

  readonly readonly = input.required<boolean>();
  readonly milestoneList = input<ProcessMilestoneListForDropdownFragment | null>(null);
  // Narrows the candidates down - the same filter the task inbox takes
  readonly candidateFilter = input<FilterMetadataMap | null>(null);
  // Only in the fullscreen dialog can a milestone be clicked to reveal its candidates
  readonly fullscreen = input<boolean>(false);
  // The schedule node's outer/inner ring, toggled independently by the shared controls
  readonly showProgress = input<boolean>(true);
  readonly showPlanningData = input<boolean>(true);
  // The band of totals under the timeline
  readonly showSummary = input<boolean>(true);
  readonly candidateSum = output<number>();

  // While the list's milestones and states are being fetched
  protected readonly isLoading = signal(false);
  // From an observable rather than emitted where the fetch ends, since that also happens on destroy, when the
  // output may no longer emit
  readonly loadingChange = outputFromObservable(toObservable(this.isLoading));

  private readonly milestoneData = signal<ScheduledMilestone[]>([]);

  // Totals for the band under the timeline
  private summary: MilestoneAggregateSummary;

  protected readonly selectedMilestoneId = signal<string | null>(null);

  // Set by clicking a milestone's bolt: the candidate table then lists only those behind it
  protected readonly isCriticalOnly = signal(false);

  protected readonly selectedMilestoneName = computed(() => {
    const id = this.selectedMilestoneId();
    return this.milestoneData().find((milestone) => milestone.id === id)?.localizedName ?? null;
  });

  private readonly listRequest = computed<MilestoneListRequest | null>(() => {
    const list = this.milestoneList();
    if (isNil(list?.id)) {
      return null;
    }

    const candidateFilter = this.candidateFilter();
    return {
      milestoneListId: list.id,
      milestoneListOrganizationId: list.organizationId,
      candidateFilter: isEmpty(candidateFilter) ? null : candidateFilter,
    };
  });

  private readonly selectedMilestoneRequest = computed<MilestoneCandidatesRequest | null>(() => {
    const listRequest = this.listRequest();
    const milestoneId = this.selectedMilestoneId();
    if (isNil(listRequest) || isNil(milestoneId)) {
      return null;
    }

    return { ...listRequest, milestoneId };
  });

  protected readonly candidateColumns: TableColumn<MilestoneCandidateRow>[] = [
    {
      header: translateSafely("name.title"),
      fieldname: nameOf("name"),
      sortable: true,
      includeInGlobalFilter: true,
    },
    {
      header: translateSafely("displayId.title"),
      fieldname: nameOf("displayId"),
      sortable: true,
      includeInGlobalFilter: true,
    },
    {
      header: translateSafely("country.title"),
      fieldname: nameOf("country"),
      sortable: true,
      includeInGlobalFilter: true,
      pipeDescription: new PipeDescription(StaticDataPipe, StaticDataType.Countries),
    },
    {
      header: translateSafely("process.title"),
      fieldname: nameOf("process"),
      sortable: true,
      includeInGlobalFilter: true,
      routeLink: (row) => row.processLink,
    },
    {
      header: translateSafely("milestones.candidates.openTasks"),
      fieldname: nameOf("openTaskCount"),
      sortable: true,
    },
    {
      header: translateSafely("endDate.planned"),
      fieldname: nameOf("plannedEndDate"),
      sortable: true,
      pipeDescription: new PipeDescription(AppDatePipe),
      tags: (row) => of(this.getDelayTags(row.delayInDays)),
      forceDisplayField: true,
      dontApplyPipeToTags: true,
    },
  ];

  // The candidates standing at the selected milestone
  protected readonly selectedMilestoneCandidates = toSignal(
    toObservable(this.selectedMilestoneRequest).pipe(
      switchMap((request) =>
        isNil(request)
          ? of<MilestoneCandidateRow[]>([])
          : this.milestoneViewService
              .fetchMilestoneCandidateStates(request, "no-cache")
              .pipe(map((states) => (states ?? []).map((state) => this.toCandidateRow(state))))
      )
    ),
    { initialValue: [] }
  );

  protected readonly visibleMilestoneCandidates = computed(() => {
    const candidates = this.selectedMilestoneCandidates();
    if (!this.isCriticalOnly()) {
      return candidates;
    }

    return candidates.filter(({ delayInDays }) => isCriticalDelay(delayInDays));
  });

  // Drilled into from the candidate table; the flat table then lists this candidate's open tasks
  protected readonly selectedCandidate = signal<MilestoneCandidateRow | null>(null);

  // A page fills the height the dialog leaves the table, so it neither scrolls nor leaves a gap
  private readonly candidateTable = viewChild("candidateTable", { read: ElementRef });
  protected readonly candidateRowsPerPage = signal(MIN_CANDIDATE_ROWS_PER_PAGE);

  // Bumped after a task was acted on or configured, so its row shows the new state
  private readonly tasksReloadCount = signal(0);

  private readonly selectedCandidateTasksRequest = computed(() => ({
    candidate: this.selectedCandidate(),
    reloadCount: this.tasksReloadCount(),
  }));

  // null while loading, so a half-loaded list isn't mistaken for tasks hidden by visibility settings
  protected readonly selectedCandidateTasks = toSignal(
    toObservable(this.selectedCandidateTasksRequest).pipe(
      switchMap(({ candidate }) =>
        isNil(candidate)
          ? of(null)
          : this.milestoneViewService
              .fetchMilestoneFlatTasks(
                uniqBy(candidate.activeTaskIds ?? [], ({ taskId }) => taskId).map(
                  ({ taskId, processId, organizationId }) => ({ taskId, processId, organizationId })
                )
              )
              .pipe(startWith(null))
      )
    ),
    { initialValue: null }
  );

  // The candidate table counts every open task, but the task query only returns those visible to the user
  protected readonly areTasksHiddenByVisibility = computed(() => {
    const candidate = this.selectedCandidate();
    const tasks = this.selectedCandidateTasks();
    if (isNil(candidate) || isNil(tasks)) {
      return false;
    }

    return tasks.length < candidate.openTaskCount;
  });

  protected readonly openedTaskAction = signal<OpenedTaskAction | null>(null);

  protected readonly taskOperations: TableOperation[] = [
    {
      label: translateSafely("task.act"),
      icon: (item: FlatTask) =>
        item.task.instanceState === InstanceStateEnum.Started ? PrimeIcons.CARET_UP : PrimeIcons.EYE,
      mode: TableOperationMode.Button,
      severity: "primary",
      buttonVariant: "text",
      canOperate: (item: FlatTask) => this.taskPermissions.canAct(item.task, this.getPermissionContext(item)),
      operation: (item: FlatTask) => this.openedTaskAction.set({ item, action: "act" }),
    },
    {
      label: translateSafely("task.configure"),
      icon: PrimeIcons.COG,
      mode: TableOperationMode.Button,
      severity: "secondary",
      buttonVariant: "text",
      // Ad-hoc tasks are configured in their own dialogs, which only the process task list offers
      canOperate: (item: FlatTask) =>
        !item.process.isAdhocProcess && this.taskPermissions.canConfigure(item.task, this.getPermissionContext(item)),
      operation: (item: FlatTask) => this.openedTaskAction.set({ item, action: "configure" }),
    },
  ];

  // The other loaded tasks of the same process, which the drawer reads a missing owner organization from
  protected readonly openedTaskSiblings = computed(() => {
    const opened = this.openedTaskAction();
    return isNil(opened) ? [] : this.getProcessTasks(opened.item);
  });

  protected readonly breadcrumbItems = computed<MenuItem[]>(() => {
    const milestoneName = this.selectedMilestoneName();
    const candidate = this.selectedCandidate();
    const backToCandidates = (): void => this.selectedCandidate.set(null);
    if (!this.isCriticalOnly()) {
      return isNil(candidate)
        ? [{ label: milestoneName }]
        : [{ label: milestoneName, command: backToCandidates }, { label: candidate.name }];
    }

    const backToMilestone = (): void => {
      this.isCriticalOnly.set(false);
      this.selectedCandidate.set(null);
    };
    const criticalLabel = translateSafely("milestones.candidates.criticalOnly", { days: CRITICAL_DELAY_IN_DAYS });
    return isNil(candidate)
      ? [{ label: milestoneName, command: backToMilestone }, { label: criticalLabel }]
      : [
          { label: milestoneName, command: backToMilestone },
          { label: criticalLabel, command: backToCandidates },
          { label: candidate.name },
        ];
  });

  resizeObserver?: ResizeObserver;

  constructor() {
    // Fetch the aggregated milestone data for the selected list, dropping a response once another list is picked
    // Refetches when the list or the candidate filter changes, dropping a response that has been overtaken
    toObservable(this.listRequest)
      .pipe(
        filter((request) => !isNil(request)),
        switchMap((request) => {
          // Set here rather than in a tap before the switchMap: an overtaken fetch is finalized only once this
          // runs, and would otherwise clear the flag the new one has just set
          this.isLoading.set(true);
          return forkJoin({
            // The list's milestones with their durations; the states add where the candidates stand
            milestoneData: this.milestoneViewService.fetchAggregatedMilestoneData(request.milestoneListId, "no-cache"),
            states: this.milestoneViewService.fetchAggregatedMilestoneStates(request, "no-cache"),
          }).pipe(finalize(() => this.isLoading.set(false)));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ milestoneData, states }) => this.onDataLoaded(milestoneData.milestoneData ?? [], states));

    // Another list has other milestones; a new filter keeps the selection and just refetches its candidates
    effect(() => {
      this.milestoneList();
      untracked(() => {
        this.selectedMilestoneId.set(null);
        this.isCriticalOnly.set(false);
      });
    });

    // A candidate drilled into belongs to the milestone it was picked at, and may not be at the next one
    effect(() => {
      this.selectedMilestoneRequest();
      untracked(() => this.selectedCandidate.set(null));
    });

    // The table's height is set by the dialog's flex layout rather than by its rows, so resizing to fit
    // cannot feed back into the height it was measured from
    effect((onCleanup) => {
      const host = this.candidateTable()?.nativeElement;
      if (isNil(host)) return;

      const observer = new ResizeObserver(() => this.candidateRowsPerPage.set(this.getCandidateRowsFitting(host)));
      observer.observe(host);
      onCleanup(() => observer.disconnect());
    });

    // The table keeps its height when the candidates arrive, so the observer above stays quiet - but only now
    // is there a real row and paginator to measure instead of the fallbacks
    effect(() => {
      this.visibleMilestoneCandidates();
      const host = this.candidateTable()?.nativeElement;
      if (isNil(host)) return;

      afterNextRender(() => this.candidateRowsPerPage.set(this.getCandidateRowsFitting(host)), {
        injector: this.injector,
      });
    });

    // Redraw when either ring or the summary is toggled or another milestone is selected, without refetching data
    effect(() => {
      this.showProgress();
      this.showPlanningData();
      this.showSummary();
      this.selectedMilestoneId();
      const container = this.timelineContainer();
      if (!container || !this.milestoneData().length) return;

      this.renderMilestones(container.nativeElement);
    });
  }

  ngAfterViewInit(): void {
    // Listen for container resize
    this.resizeObserver = new ResizeObserver(() => {
      const container = this.timelineContainer();
      if (this.milestoneData().length > 0) this.renderMilestones(container.nativeElement);
    });
    this.resizeObserver.observe(this.timelineContainer().nativeElement);
  }

  protected onTaskChanged(): void {
    this.tasksReloadCount.update((count) => count + 1);
  }

  protected onCandidateSelected(candidate: MilestoneCandidateRow | null): void {
    if (isNil(candidate)) {
      return;
    }

    this.selectedCandidate.set(candidate);
  }

  /**
   * The same pill the runtime process graph puts on a task box. Nothing when on plan, and nothing for
   * dropped out candidates, who have no delay.
   */
  /**
   * The same rules the process task list and graph decide by, judged against the task's own process,
   * since the tasks of one candidate can come from several.
   */
  private getPermissionContext(item: FlatTask): ProcessTaskPermissionContext {
    const { process } = item;
    const roles = this.processHelper.getUserRoles(process);
    return {
      process,
      // A task names its process through rootId, which for a delegated process is the id before the delegation
      userRoleMap: isNil(process.delegatedProcessId)
        ? { [process.id]: roles }
        : { [process.id]: roles, [process.delegatedProcessId]: roles },
      allTasks: this.getProcessTasks(item),
    };
  }

  private getProcessTasks(item: FlatTask): FlatTask["task"][] {
    return (this.selectedCandidateTasks() ?? [])
      .filter(({ process }) => process.id === item.process.id)
      .map(({ task }) => task);
  }

  private getCandidateRowsFitting(host: HTMLElement): number {
    const captionHeight = host.querySelector<HTMLElement>(".p-datatable-header")?.offsetHeight ?? 0;
    const headerHeight = host.querySelector<HTMLElement>("thead")?.offsetHeight ?? 0;
    const paginatorHeight = host.querySelector<HTMLElement>(".p-paginator")?.offsetHeight ?? PAGINATOR_HEIGHT_FALLBACK;
    // A row not laid out yet measures 0, which is no height to divide by
    const measuredRowHeight = host.querySelector<HTMLElement>("tbody tr")?.offsetHeight ?? 0;
    const rowHeight = measuredRowHeight > 0 ? measuredRowHeight : CANDIDATE_ROW_HEIGHT_FALLBACK;
    const availableHeight =
      host.clientHeight - captionHeight - headerHeight - paginatorHeight - CANDIDATE_TABLE_SCROLLBAR_OFFSET;
    const rowsFitting = Math.floor(availableHeight / rowHeight);
    return Math.max(MIN_CANDIDATE_ROWS_PER_PAGE, rowsFitting);
  }

  private getDelayTags(delayInDays: number | nil): TableTag[] {
    if (isNil(delayInDays) || delayInDays === 0) {
      return [];
    }

    const days = Math.abs(delayInDays);
    if (delayInDays > 0) {
      return [
        {
          label:
            days === 1
              ? translateSafely("process.graph.progress.delayedSingular")
              : translateSafely("process.graph.progress.delayed", { days }),
          severity: "danger",
          icon: "icon-[mi--arrow-up]",
        },
      ];
    }

    return [
      {
        label:
          days === 1
            ? translateSafely("process.graph.progress.aheadSingular")
            : translateSafely("process.graph.progress.ahead", { days }),
        severity: "success",
        icon: "icon-[mi--arrow-down]",
      },
    ];
  }

  private toCandidateRow(state: MilestoneCandidateStateFragment): MilestoneCandidateRow {
    const firstProcess = state.processes.at(0);
    return {
      id: state.candidateId,
      name: state.candidateDisplayName,
      displayId: state.candidateDisplayId,
      country: state.candidateCountry,
      process: state.processes.map(({ displayName }) => displayName).join(", "),
      processLink: isNil(firstProcess)
        ? null
        : ["/app/processes/view", firstProcess.organizationId, firstProcess.processId],
      openTaskCount: uniqBy(state.activeTaskIds, ({ taskId }) => taskId).length,
      plannedEndDate: state.endDate,
      delayInDays: state.delayInDays,
      activeTaskIds: state.activeTaskIds,
    };
  }

  private onDataLoaded(milestones: GlobalMilestoneWithMeta[], states: AggregatedMilestoneStates): void {
    if (!milestones.length) {
      this.milestoneData.set([]);
      this.candidateSum.emit(null);
      return;
    }

    const distributionByMilestoneId = new Map(
      (states?.milestones ?? []).map(({ milestoneId, scheduleDistribution }) => [
        milestoneId,
        toScheduleBreakdown(scheduleDistribution),
      ])
    );
    this.milestoneData.set(
      milestones.map((milestone) => {
        const scheduleDistribution = distributionByMilestoneId.get(milestone.id);
        return {
          ...milestone,
          scheduleDistribution,
          // The node counts the candidates standing at the milestone: still on it, or dropped out there
          index: isNil(scheduleDistribution) ? 0 : countStandingAt(scheduleDistribution),
          isCritical: isCriticalDelay(scheduleDistribution?.behind.maxDelayInDays),
        };
      })
    );

    const candidateCount = states?.candidateCount ?? 0;
    this.candidateSum.emit(candidateCount);
    this.summary = {
      totalCandidates: candidateCount,
      distribution: isNil(states?.summaryDistribution) ? undefined : toScheduleBreakdown(states.summaryDistribution),
    };

    const container = this.timelineContainer();
    if (container) this.renderMilestones(container.nativeElement);
  }

  private renderMilestones(container: HTMLElement): void {
    // A redraw replaces the hovered element without a mouseleave - e.g. clicking the bolt - which would leave
    // its tooltip open
    this.onTimelineTooltip(null);
    this.timelineRendererService.renderTimeline(container, this.milestoneData(), {
      width: container.offsetWidth || 1100,
      rowHeight: 220,
      // Equal, since the schedule node is a disc of this diameter and connectors anchor to its edge
      nodeWidth: 96,
      nodeHeight: 96,
      borderRadius: 16,
      showDuration: true,
      showSubtitle: false,
      showScheduleDistribution: true,
      showProgress: this.showProgress(),
      showPlanningData: this.showPlanningData(),
      summary: this.summary,
      showSummary: this.showSummary(),
      onMilestoneClick: this.fullscreen() ? (id): void => this.onMilestoneClicked(id) : undefined,
      onCriticalClick: this.fullscreen() ? (id): void => this.onCriticalClicked(id) : undefined,
      criticalTooltip: this.criticalTooltipText(),
      onTooltip: (tooltip): void => this.onTimelineTooltip(tooltip),
      // Nobody standing there means an empty table to drill into
      isMilestoneClickable: (milestone) => (milestone.index ?? 0) > 0,
      selectedMilestoneId: this.selectedMilestoneId(),
    });
  }

  private onMilestoneClicked(id: string | number): void {
    const milestone = this.milestoneData().find((candidate) => candidate.id === id);
    if (isNil(milestone)) return;

    this.isCriticalOnly.set(false);
    this.selectedMilestoneId.set(milestone.id);
  }

  private onTimelineTooltip(tooltip: TimelineTooltip | null): void {
    // Closed before every change, so moving straight from one target to the next reopens it in the new place
    this.timelineTooltip().deactivate();
    if (isNil(tooltip)) {
      this.tooltipPlacement.set(null);
      return;
    }

    const targetRect = tooltip.target.getBoundingClientRect();
    const hostRect = this.hostElement.nativeElement.getBoundingClientRect();
    this.tooltipText.set(tooltip.text);
    this.tooltipPlacement.set({
      left: targetRect.left - hostRect.left,
      top: targetRect.top - hostRect.top,
      width: targetRect.width,
      height: targetRect.height,
    });
    // Only once the anchor has moved over the target and taken its text, since the tooltip positions itself
    // off the anchor - and not at all if the pointer has left again in the meantime
    afterNextRender(
      () => {
        if (!isNil(this.tooltipPlacement())) {
          this.timelineTooltip().activate();
        }
      },
      { injector: this.injector }
    );
  }

  private onCriticalClicked(id: string | number): void {
    const milestone = this.milestoneData().find((candidate) => candidate.id === id);
    if (isNil(milestone)) return;

    // Also when the milestone was selected already, where the effect resetting the candidate does not fire
    this.selectedCandidate.set(null);
    this.isCriticalOnly.set(true);
    this.selectedMilestoneId.set(milestone.id);
  }
}

function toScheduleSegment(segment: MilestoneScheduleSegmentFragment): MilestoneScheduleSegment {
  return {
    count: segment.count,
    averageDelayInDays: segment.averageDelayInDays ?? undefined,
    minDelayInDays: segment.minDelayInDays ?? undefined,
    maxDelayInDays: segment.maxDelayInDays ?? undefined,
  };
}

function toScheduleBreakdown(breakdown: MilestoneScheduleBreakdownFragment): MilestoneScheduleBreakdown {
  return {
    completed: toScheduleSegment(breakdown.completed),
    ahead: toScheduleSegment(breakdown.ahead),
    onTime: toScheduleSegment(breakdown.onTime),
    behind: toScheduleSegment(breakdown.behind),
    droppedOut: toScheduleSegment(breakdown.droppedOut),
  };
}

function isCriticalDelay(delayInDays: number | nil): boolean {
  return (delayInDays ?? 0) > CRITICAL_DELAY_IN_DAYS;
}

function countStandingAt(breakdown: MilestoneScheduleBreakdown): number {
  const { ahead, onTime, behind, droppedOut } = breakdown;
  return [ahead, onTime, behind, droppedOut].reduce((sum, { count }) => sum + count, 0);
}
